import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    enumMapper,
    FeatureFlag,
    IJob,
    IJobUpdated,
    JobSource,
    JobState,
    JobType,
} from '@models';
import {
    GRID_SERVICE,
    ODataGridComponent,
    STATE_HANDLER,
    urlStateHandler,
} from '@pages/generics/grid';
import {
    JobDetailsModalComponent,
    JobModalComponent,
} from '@pages/jobs/modals';
import {
    FileDownloadService,
    JobService,
    ProcessService,
    SignalRService,
} from '@services';

import {
    BehaviorSubject,
    Observable,
    of,
} from 'rxjs';
import {
    filter,
    map,
    switchMap,
    takeUntil,
} from 'rxjs/operators';

@Component({
    templateUrl: './jobs.page.html',
    styleUrls: ['./jobs.page.scss'],
    providers: [{
        provide: GRID_SERVICE,
        useClass: JobService,
    },
    {
        provide: STATE_HANDLER,
        useValue: urlStateHandler,
    }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class JobsPageComponent extends ODataGridComponent<IJob, number> implements OnInit, OnDestroy {

    public isCurrentFolderClassic$!: Observable<boolean>;

    public get isMediaRecordingEnabled() {
        return this._appState.settings.isFeatureEnabled(FeatureFlag.MediaRecording);
    }

    public get allowCancel(): boolean {
        return this._grid.selectionManager.hasValue()
            && this._grid.selectionManager.selected.every((job: IJob) => {
                return this.isCancellingAllowed(job);
            });
    }

    public get allowTerminate(): boolean {
        return this._grid.selectionManager.hasValue()
            && this._grid.selectionManager.selected.every((job: IJob) => {
                return this.isTerminationAllowed(job);
            });
    }

    public JobSource = JobSource;
    public JobType = JobType;
    public JobState = JobState;

    public stateList = enumMapper(JobState, {
        resourceKey: 'JobState',
    });

    public sourceList = enumMapper(JobSource, {
        resourceKey: 'JobSource',
    });

    public disableDetails$ = new BehaviorSubject(false);
    public isLicenseInvalid$ = this._appState.user.license.value$
        .pipe(
            map(license => license!.isExpired),
        );

    public isManualRestartFeatureEnabled = this._appState.settings.isFeatureEnabled(FeatureFlag.ManuallyRestartJob);

    private _route = this._injector.get<ActivatedRoute>(ActivatedRoute);

    private _signalR = this._injector.get<SignalRService>(SignalRService);

    private _jobService = this._service as JobService;
    private _processService = this._injector.get<ProcessService>(ProcessService);
    private _downloadService = this._injector.get<FileDownloadService>(FileDownloadService);

    ngOnInit() {
        this._signalR.watchNewJobs();

        this._signalR
            .jobs$
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(this._updateJobState);

        this._expand$.next(['Robot', 'Release']);

        this.data$
            .subscribe(jobs => this._signalR.watchJobs(jobs.map(job => job.id)));

        this._route.paramMap
            .pipe(
                map(paramMap => parseInt(paramMap.get('id')!, 10)),
                filter(id => !isNaN(id)),
                takeUntil(this._destroyed$),
            )
            .subscribe(id => {
                this._filters$.next([{
                    value: id,
                    property: 'startingTriggerId',
                    method: 'eq',
                }]);
            });

        this.isCurrentFolderClassic$ = this._appState.user.folder.isClassic$;
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this._signalR.watchJobs(false);
        this._signalR.watchNewJobs(false);
    }

    public openModal(job?: IJob) {
        this._fetchProcessAndJob(job)
            .pipe(
                switchMap(completeJob =>
                    this._modal
                        .openModal(JobModalComponent, completeJob, { size: 'lg' })
                        .afterClosedResult(),
                ),
            ).subscribe(() => this._refresh$.next());
    }

    public showDetails(job: IJob) {
        this.disableDetails$.next(true);
        this._jobService
            .getInstance(job.id, ['Robot', 'Release'])
            .pipe(takeUntil(this._destroyed$))
            .subscribe((jobDetails: IJob) => {
                this._modal.openModal(JobDetailsModalComponent, jobDetails);
                this.disableDetails$.next(false);
            });
    }

    public stopJob(kill: boolean, job?: IJob) {
        this._modal.onConfirm(
            null,
            () => {
                const singleAction = job != null;
                const serviceCall$ = singleAction ?
                    this._jobService
                        .stopJob(job!.id, kill) :
                    this._jobService
                        .stopJobs(this._grid.selectionManager.selected.map(j => j.id), kill);

                serviceCall$.subscribe(() => this._grid.selectionManager.clear());
            },
            () => { },
            this._translate.instant(kill ? 'jobs_confirm_kill_message' : 'jobs_confirm_stop_message'),
        );
    }

    public isResumeAllowed(job: IJob): boolean {
        return job.state === JobState[JobState.Suspended] && job.type !== JobType[JobType.Attended];
    }

    public resumeJob(job?: IJob) {
        this._modal.onConfirm(
            null,
            () => {
                this._jobService
                    .resumeJob(job!.key)
                    .subscribe(() => this._grid.selectionManager.clear());
            },
            () => { },
            this._translate.instant('jobs_confirm_resume_message'),
        );
    }

    public isCancellingAllowed(job: IJob): boolean {
        return [JobState.Pending, JobState.Running, JobState.Suspended, JobState.Resumed]
            .map(js => JobState[js])
            .indexOf(job.state) >= 0 &&
            job.type !== JobType[JobType.Attended];
    }

    public isTerminationAllowed(job: IJob): boolean {
        return [JobState.Pending, JobState.Running, JobState.Stopping, JobState.Suspended, JobState.Resumed]
            .map(js => JobState[js])
            .indexOf(job.state) >= 0 &&
            job.type !== JobType[JobType.Attended];
    }

    public isRestartAllowed(job: IJob): boolean {
        return [JobState.Faulted, JobState.Successful, JobState.Stopped]
            .map(js => JobState[js])
            .indexOf(job.state) >= 0 &&
            job.sourceType !== JobSource[JobSource.Agent];
    }

    public downloadRecording(job: IJob) {
        this._downloadService.download(
            this._jobService.getMediaDownloadUrl(job),
        );
    }

    private _updateJobState = (message: IJobUpdated) => {
        if (
            message.isNew &&
            this._grid.footer!.state.pageIndex === 0
        ) {
            this._refresh$.next();
        } else {
            this._grid.dataManager.patchRow(message.jobId, {
                state: message.state,
                startTime: message.startTime,
                endTime: message.endTime,
                ...!message.robotName ? { robot: undefined } :
                    {
                        robot: {
                            name: message.robotName,
                        },
                    },
            });
        }
    }

    private _fetchProcessAndJob = (job?: IJob): Observable<IJob | undefined> =>
        !!job
            ? this._processService
                .getInstance(job.release.id)
                .pipe(
                    // we need to fetch the associated robot because
                    // signalr updates don't include the robot id
                    switchMap(() => this._jobService.getInstance(job.id, ['Robot', 'Release'])),
                    takeUntil(this._destroyed$),
                )
            : of(undefined)
}
